import { useQuery } from "@tanstack/react-query";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Box from "@cloudscape-design/components/box";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import Table from "@cloudscape-design/components/table";
import BarChart from "@cloudscape-design/components/bar-chart";
import { fetchSimtTickets } from "../api";
import type { Ticket, Severity } from "../types";

function calculateMetrics(tickets: Ticket[]) {
  const now = new Date();
  const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

  const openTickets = tickets.filter((t) => t.status !== "Resolved" && t.status !== "Closed");
  const recentlyOpened = tickets.filter((t) => new Date(t.createdAt) >= twoWeeksAgo);

  const avgAge =
    openTickets.length > 0
      ? Math.round(openTickets.reduce((sum, t) => sum + t.daysOpen, 0) / openTickets.length * 10) / 10
      : 0;

  const bySeverity: Record<Severity, { opened: number; open: number }> = {
    SEV_2: { opened: 0, open: 0 },
    SEV_3: { opened: 0, open: 0 },
    SEV_4: { opened: 0, open: 0 },
    SEV_5: { opened: 0, open: 0 },
  };

  for (const t of recentlyOpened) {
    bySeverity[t.severity].opened++;
  }
  for (const t of openTickets) {
    bySeverity[t.severity].open++;
  }

  const aging: Record<string, number> = {
    "0-3 days": 0,
    "3-7 days": 0,
    "7-14 days": 0,
    "14-30 days": 0,
    "30+ days": 0,
  };

  for (const t of openTickets) {
    if (t.daysOpen <= 3) aging["0-3 days"]++;
    else if (t.daysOpen <= 7) aging["3-7 days"]++;
    else if (t.daysOpen <= 14) aging["7-14 days"]++;
    else if (t.daysOpen <= 30) aging["14-30 days"]++;
    else aging["30+ days"]++;
  }

  // SLA: Sev-2 = same day, Sev-3 = 2 days, Sev-4 = 5 days, Sev-5 = 5 days
  const slaThresholds: Record<Severity, number> = {
    SEV_2: 1,
    SEV_3: 2,
    SEV_4: 5,
    SEV_5: 5,
  };

  const slaCompliance: Record<Severity, number> = { SEV_2: 1, SEV_3: 1, SEV_4: 1, SEV_5: 1 };
  for (const sev of Object.keys(slaThresholds) as Severity[]) {
    const sevTickets = openTickets.filter((t) => t.severity === sev);
    if (sevTickets.length === 0) continue;
    const withinSla = sevTickets.filter((t) => t.daysOpen <= slaThresholds[sev]).length;
    slaCompliance[sev] = Math.round((withinSla / sevTickets.length) * 100) / 100;
  }

  return {
    totalOpen: openTickets.length,
    recentlyOpened: recentlyOpened.length,
    avgAge,
    bySeverity,
    aging,
    slaCompliance,
  };
}

export default function MetricsPage() {
  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ["tickets"],
    queryFn: fetchSimtTickets,
  });

  const metrics = calculateMetrics(tickets);

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Last 14 days">
        PIRT Metrics
      </Header>

      {/* Summary cards */}
      <ColumnLayout columns={4} variant="text-grid">
        <Container>
          <Box variant="awsui-key-label">Currently Open</Box>
          <Box variant="awsui-value-large">{isLoading ? "—" : metrics.totalOpen}</Box>
        </Container>
        <Container>
          <Box variant="awsui-key-label">Opened (14d)</Box>
          <Box variant="awsui-value-large">{isLoading ? "—" : metrics.recentlyOpened}</Box>
        </Container>
        <Container>
          <Box variant="awsui-key-label">Avg Age (days)</Box>
          <Box variant="awsui-value-large">{isLoading ? "—" : metrics.avgAge}</Box>
        </Container>
        <Container>
          <Box variant="awsui-key-label">Sev-2/3 SLA</Box>
          <Box variant="awsui-value-large">
            {isLoading
              ? "—"
              : `${Math.round(((metrics.slaCompliance.SEV_2 + metrics.slaCompliance.SEV_3) / 2) * 100)}%`}
          </Box>
        </Container>
      </ColumnLayout>

      {/* Severity breakdown */}
      <Container header={<Header variant="h2">Open by Severity</Header>}>
        <BarChart
          series={[
            {
              title: "Open",
              type: "bar",
              data: [
                { x: "Sev-2", y: metrics.bySeverity.SEV_2.open },
                { x: "Sev-3", y: metrics.bySeverity.SEV_3.open },
                { x: "Sev-4", y: metrics.bySeverity.SEV_4.open },
                { x: "Sev-5", y: metrics.bySeverity.SEV_5.open },
              ],
            },
          ]}
          xTitle="Severity"
          yTitle="Tickets"
          height={250}
          hideFilter
          empty={<Box>No data</Box>}
          noMatch={<Box>No data</Box>}
        />
      </Container>

      {/* Aging distribution */}
      <Container header={<Header variant="h2">Aging Distribution</Header>}>
        <BarChart
          series={[
            {
              title: "Tickets",
              type: "bar",
              data: Object.entries(metrics.aging).map(([x, y]) => ({ x, y })),
            },
          ]}
          xTitle="Age"
          yTitle="Count"
          height={250}
          hideFilter
          empty={<Box>No data</Box>}
          noMatch={<Box>No data</Box>}
        />
      </Container>

      {/* SLA compliance table */}
      <Container header={<Header variant="h2">SLA Compliance</Header>}>
        <Table
          columnDefinitions={[
            { id: "severity", header: "Severity", cell: (item) => item.severity },
            { id: "target", header: "SLA Target", cell: (item) => item.target },
            { id: "compliance", header: "Compliance", cell: (item) => `${Math.round(item.compliance * 100)}%` },
          ]}
          items={[
            { severity: "Sev-2", target: "Same day", compliance: metrics.slaCompliance.SEV_2 },
            { severity: "Sev-3", target: "2 business days", compliance: metrics.slaCompliance.SEV_3 },
            { severity: "Sev-4", target: "5 business days", compliance: metrics.slaCompliance.SEV_4 },
            { severity: "Sev-5", target: "5 business days", compliance: metrics.slaCompliance.SEV_5 },
          ]}
          variant="embedded"
        />
      </Container>
    </SpaceBetween>
  );
}
